import { useEffect, useState, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { popupApiAdapter } from '../../adapters/PopupApiAdapter';
import { PopupModal } from '../display/PopupModal';

const POPUP_QUERY_KEY = ['my-popups'];
const FETCH_INTERVAL_MS = 5 * 60 * 1000;

export const PopupConsumerManager = () => {
  const queryClient = useQueryClient();

  const [currentPopupIndex, setCurrentPopupIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isMarkingRead, setIsMarkingRead] = useState(false);

  const processedPopupIds = useRef<Set<number>>(new Set());

  const {
    data: popups = [],
    isLoading
  } = useQuery({
    queryKey: POPUP_QUERY_KEY,
    queryFn: () => popupApiAdapter.getMyPopups({ limit: 20 }),
    staleTime: FETCH_INTERVAL_MS,
    refetchOnWindowFocus: false,
    refetchInterval: FETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  const unprocessedPopups = popups.filter(
    popup => !processedPopupIds.current.has(popup.id)
  );

  useEffect(() => {
    if (unprocessedPopups.length > 0 && !isLoading && !isVisible) {
      setIsVisible(true);
      setCurrentPopupIndex(0);
    }
  }, [unprocessedPopups.length, isLoading, isVisible]);

  const markCurrentAsRead = useCallback(async () => {
    const currentPopup = unprocessedPopups[currentPopupIndex];

    if (!currentPopup) return;

    setIsMarkingRead(true);

    try {
      await popupApiAdapter.markPopupAsRead(currentPopup.id);
      processedPopupIds.current.add(currentPopup.id);
    } catch (error) {
      console.error('Error marking popup as read:', error);
    } finally {
      setIsMarkingRead(false);
    }
  }, [unprocessedPopups, currentPopupIndex]);

  const handleNext = useCallback(async () => {
    await markCurrentAsRead();

    const nextIndex = currentPopupIndex + 1;
    if (nextIndex < unprocessedPopups.length) {
      setCurrentPopupIndex(nextIndex);
    } else {
      setIsVisible(false);
      setCurrentPopupIndex(0);
      queryClient.invalidateQueries({ queryKey: POPUP_QUERY_KEY });
    }
  }, [currentPopupIndex, unprocessedPopups.length, markCurrentAsRead, queryClient]);

  if (!isVisible || unprocessedPopups.length === 0) {
    return null;
  }

  const currentPopup = unprocessedPopups[currentPopupIndex];

  if (!currentPopup) {
    return null;
  }

  return (
    <PopupModal
      popup={currentPopup}
      isVisible={isVisible}
      currentIndex={currentPopupIndex + 1}
      totalCount={unprocessedPopups.length}
      onNext={handleNext}
      isLoading={isMarkingRead}
    />
  );
};
